import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { ConditionalAccess } from '../../../admin/components/ConditionalAccess';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { isTemplateNew } from '../../../models/UiUtils';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { ITemplateCard } from '../../../viewmodels/AppViewModels';
import { getTemplateApprovalStatus } from '../../components/TemplateReviewStatusIndicator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { ExpiryIcon } from '../../components/svgs/icons/ExpiryIcon';
import {
	CampaignSummaryFromPreview,
	CampaignTemplateCardMenu,
} from '../../components/templateCards/CampaignTemplateCard/presentation';
import { Button } from '../Button';
import { THEME } from '../Theme/theme';
import { styleSheet } from './styles';

export interface CampaignTemplateCardProps extends IImpersonationContextComponentProps {
	card: ITemplateCard;
	ctaText?: string;
	className?: string;
	copyMenu?: React.ReactNode;
	onCopyDirectLinkClicked?: () => void;
	industry?: Api.Industry;
	isAccountTemplate?: boolean;
	isFeatured?: boolean;
	onCtaClicked?(e?: React.MouseEvent<Element>): void;
	onDelete?(): void;
	onShare?(isShared: boolean): void;
	onSocialClicked?(e: React.MouseEvent<Element>): void;
	styles?: StyleDeclarationValue[];
	useWholeCardAsCTA?: boolean;
}

const CampaignTemplateCardBase: React.FC<CampaignTemplateCardProps> = ({
	card,
	copyMenu,
	isAccountTemplate,
	isFeatured,
	onCtaClicked,
	onCopyDirectLinkClicked,
	onDelete,
	onShare,
	styles = [],
}) => {
	const expirationDate = card.schedule?.expirationDate ? moment(card.schedule.expirationDate) : null;
	const userSession = useUserSession();

	const isNew = isTemplateNew(card);
	const approvalStatus = getTemplateApprovalStatus(card?.templateReview);

	return (
		<div className={css(styleSheet.container, isFeatured && styleSheet.featured, ...styles)}>
			{(isNew || expirationDate || copyMenu) && (
				<div className={css(styleSheet.topContainer)}>
					<div className={css(styleSheet.infoContainer)}>
						{isNew && <span className={css(styleSheet.newBadge)}>New!</span>}
						{expirationDate && (
							<Tooltip>
								<TooltipTrigger>
									<ExpiryIcon fill={THEME.colors.hover.main} />
								</TooltipTrigger>
								<TooltipContent styles={[styleSheet.tooltipContent]}>
									<span>
										{`This is a time-sensitive campaign. Emails will not be sent after ${expirationDate.format('MMM D, YYYY')}.`}
									</span>
								</TooltipContent>
							</Tooltip>
						)}
					</div>

					<ConditionalAccess permission={AdminPermission.CreateCampaigns}>
						<div className={css(styleSheet.actionsContainer)}>{copyMenu}</div>
					</ConditionalAccess>

					{isAccountTemplate ? (
						<CampaignTemplateCardMenu card={card} userSession={userSession} onDelete={onDelete} onShare={onShare} />
					) : null}
				</div>
			)}

			{/* Header with title and expiration */}
			<div className={css(styleSheet.headerContainer)}>
				<div className={css(styleSheet.titleContainer)}>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant='link' styles={[styleSheet.titleButton]} onClick={onCtaClicked}>
								{card.name || 'Custom Post'}
							</Button>
						</TooltipTrigger>
						<TooltipContent styles={[styleSheet.tooltipContent]}>{card.name ?? 'Custom Post'}</TooltipContent>
					</Tooltip>
				</div>
			</div>

			{/* Content summary */}
			{card.previewHtml && (
				<div className={css(styleSheet.contentContainer)}>
					<CampaignSummaryFromPreview card={card} />
				</div>
			)}

			{/* Approval status */}
			{approvalStatus}

			{onCopyDirectLinkClicked && (
				<div className={css(styleSheet.copyDirectLinkContainer)}>
					<Button variant='link' onClick={onCopyDirectLinkClicked}>
						Copy Direct Link
					</Button>
				</div>
			)}
		</div>
	);
};

export const CampaignTemplateCard = inject(ImpersonationContextKey)(CampaignTemplateCardBase);
